import type { QueryStatusName } from '@hub/bearing-hub/common/ui/IbhQueryStatusHandler/IbhQueryStatusHandler';
import { useTranslation } from 'next-i18next';
import { BearingsDesktopTable } from '@hub/bearing-hub/BearingHubPage/BearingsTable/BearingsDesktopTable';
import { useCallback, useEffect, useState } from 'react';
import { ProductTileList } from '@hub/bearing-hub/BearingHubPage/BearingsTable/ProductTileList';
import { NothingFound } from '@hub/bearing-hub/BearingHubPage/BearingsTable/NothingFound';
import type { IbhFilterValues } from '@hub/hooks/states/useIbhFilterValuesStore';
import { SimilarPartsTable } from '@hub/bearing-hub/BearingHubPage/BearingsTable/SimilarPartsTable';
import { useDimensionsStore } from '@hub/hooks/states/useIbhDimensionsValuesStore';
import { ProductSort } from '@hub/bearing-hub/BearingHubPage/BearingsTable/ProductSort';
import type { Headers } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import { useBearingsTableStore } from '@hub/bearing-hub/BearingHubPage/BearingsTable/useBearingsTableStore';
import type { ShapeDescriptor } from '@hub/bearing-hub/common/shapes/shapes';
import type { IbhPartsResponseDto } from '@igus/iglidur-bearing-hub-api';
import { Button, TYPE } from '@hub/common/Button/Button';
import type {
  IbhCustomArticleFileLinkParams,
  IbhIcnPartsResponseDto,
  IbhPartsLifetimeResponseDto,
} from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { IbhFileType } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { useLifetimeParametersStore } from '@hub/bearing-hub/libs/BearingsLifetime/useIbhLifetimeParametersValuesStore';
import { ibhApiService } from '@hub/bearing-hub/common/api-service';
import { useSessionStore } from '@hub/hooks/states/useSessionStore';
import { FileExtension, useFileDownload } from '@hub/common/hooks/useFileDownload';
import type { CartArticleData } from '@hub/bearing-hub/common/basket/useAddToShoppingCart';
import type { IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import { ShapeDimensionsDefinitionForm } from '@hub/bearing-hub/common/dimensions/dimensions';
import type { UnitSystem } from '@igus/kopla-domain';
import { compareBearingTableValues } from '@hub/bearing-hub/BearingHubPage/BearingsTable/bearingsTableSorting.helper';

export interface BearingsTableProps {
  isLoading: boolean;
  lastKnownValidResponse: IbhPartsResponseDto[] | IbhIcnPartsResponseDto[];
  similarParts?: IbhPartsResponseDto[];
  currentPage: number;
  maxLifeTimeReference: number;
  bearingPartsResponseStatus: QueryStatusName;
  quantity: number;
  totalPages: number;
  selectedShape: ShapeDescriptor;
  filterValues: IbhFilterValues;
  onChoose?: (dimensions: IbhDimensions) => void;
  setActiveModal?: (state: boolean) => void;
  clientReady: boolean;
  unitSystem: UnitSystem;
  articlesAddingToBasket: string[];
  onQuantityChange: (value?: number) => void;
  addToShoppingCart: (articleData: CartArticleData, articleThumbnail?: string) => void;
  bearingPartsLifetime: IbhPartsLifetimeResponseDto;
  isLifetimeLoading: boolean;
}

export interface ProductTableProps {
  data: IbhPartsResponseDto | IbhIcnPartsResponseDto;
  maxLifeTimeReference: number;
  quantity: number;
  isCustom?: boolean;
  selectedShape: ShapeDescriptor;
  lifetime: number;
  isLifetimeLoading: boolean;
  setActiveModal?: (state: boolean) => void;
  unitSystem: UnitSystem;
  clientReady: boolean;
}

export const BearingsTable = ({
  isLoading,
  lastKnownValidResponse,
  similarParts,
  currentPage,
  maxLifeTimeReference,
  bearingPartsResponseStatus,
  quantity,
  totalPages,
  selectedShape,
  filterValues,
  onChoose,
  setActiveModal,
  clientReady,
  unitSystem,
  onQuantityChange,
  articlesAddingToBasket,
  addToShoppingCart,
  bearingPartsLifetime,
  isLifetimeLoading,
}: BearingsTableProps) => {
  const { t } = useTranslation(['common', 'category/categoryPage']);
  const [tableData, setTableData] = useState(lastKnownValidResponse);
  const { currentSortingHeaderState, currentSortingHeaderKey, tableHeaders } = useBearingsTableStore();
  const dimensions = useDimensionsStore(state => state.dimensions);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [selectedParts, setSelectedParts] = useState<string[]>([]);
  const { formUnitSystem, getLifetimeConfigPayload } = useLifetimeParametersStore();
  const session = useSessionStore(state => state.session);
  const [showMultiReportOption, setShowMultiReportOption] = useState(false);
  const visibleMaxLifeTimeReference = lastKnownValidResponse
    .map(
      (article: IbhPartsResponseDto | IbhIcnPartsResponseDto) =>
        bearingPartsLifetime?.lifetimes?.[article.articleNumber] ?? 0
    )
    .reduce((max, lifetime) => Math.max(max, lifetime), 0);
  const lifetimeBarReference = visibleMaxLifeTimeReference || maxLifeTimeReference;

  const { downloadFileFromLink, loadingDownloadItem, downloadErrorHandler, setLoadingDownloadItem } = useFileDownload({
    appType: 'IBH',
    fileName: selectedParts.join(),
  });

  const handleRowExpand = useCallback((articleNumber: string, isExpanded: boolean) => {
    setExpandedRows(prev => {
      const existingRows = new Set(prev);
      if (isExpanded) {
        existingRows.add(articleNumber);
      } else {
        existingRows.delete(articleNumber);
      }
      return existingRows;
    });
  }, []);

  const handlePartSelection = (articleNumber: string) => {
    const updatedPartList = [...selectedParts];
    if (updatedPartList.includes(articleNumber)) {
      setSelectedParts(updatedPartList.filter(part => part !== articleNumber));
    } else {
      setSelectedParts([...updatedPartList, articleNumber]);
    }
  };

  const downloadMultiReport = async () => {
    if (selectedParts.length > 8) {
      return;
    }

    const customParams = {
      fileType: IbhFileType.Report,
      articleNumber: selectedParts.join(),
      shape: selectedShape.shapeKey,
      sid: session.sessionId,
      quantity,
      unitSystem,
      lifetimeUnitSystem: formUnitSystem,
      ...dimensions[selectedShape.shapeKey],
      ...getLifetimeConfigPayload(true),
    } satisfies IbhCustomArticleFileLinkParams;

    let downloadLink: string;

    try {
      setLoadingDownloadItem(prev => {
        const reports = new Set(prev);
        reports.add(0);
        return reports;
      });
      const response = await ibhApiService.customArticleFileLink(customParams);
      downloadLink = response.data.downloadLink;
      downloadFileFromLink(downloadLink, IbhFileType.Report, FileExtension.PDF, 0);
    } catch (error) {
      downloadErrorHandler(error, 0);
    }
  };

  const onAddToShoppingCartSelectedParts = () => {
    const selectedPartsData = selectedParts.map(partNumber =>
      tableData.find(article => article.articleNumber === partNumber)
    );

    selectedPartsData.forEach(part => {
      void addToShoppingCart(part);
    });
  };

  const getValueToSort = (element: IbhPartsResponseDto, columnKey: Headers) => {
    switch (columnKey) {
      case 'material':
        return element.material.name;
      case 'deliveryTime':
        return element.deliveryTimeInDays;
      case 'lifetime':
        return bearingPartsLifetime?.lifetimes?.[element.articleNumber] ?? -1;
      case 'price':
        return element.unitPrice.value;
      default:
        return element.dimensions[columnKey as keyof IbhDimensions];
    }
  };

  const getSortedData = (dataToSort: IbhPartsResponseDto[]): IbhPartsResponseDto[] =>
    [...dataToSort].sort((a, b) =>
      compareBearingTableValues(
        getValueToSort(a, currentSortingHeaderKey),
        getValueToSort(b, currentSortingHeaderKey),
        currentSortingHeaderState
      )
    );

  useEffect(() => {
    const requiredDimensions = ShapeDimensionsDefinitionForm[selectedShape.shapeKey];
    const visible = requiredDimensions.every(dimension => !!dimensions[selectedShape.shapeKey]?.[dimension]);

    setShowMultiReportOption(visible);
  }, [dimensions]);

  useEffect(() => {
    setTableData(getSortedData(lastKnownValidResponse));
  }, [lastKnownValidResponse, tableHeaders, bearingPartsLifetime]);

  useEffect(() => {
    setExpandedRows(new Set());
  }, [tableData]);

  useEffect(() => {
    setSelectedParts([]);
  }, [lastKnownValidResponse]);

  const dimensionsFormIsCompleted = (): boolean => {
    if (filterValues.dimensions) {
      const filterDimensionsKeys = Object.keys(filterValues.dimensions).filter(key => {
        const filterDimensions = filterValues.dimensions as IbhDimensions;
        return filterDimensions[key as keyof IbhDimensions];
      });

      const expectedFilterDimensionsKeys = ShapeDimensionsDefinitionForm[selectedShape.shapeKey];
      return filterDimensionsKeys.length === expectedFilterDimensionsKeys.length;
    }
    return false;
  };

  const multiReportText = (): string => {
    const count = selectedParts.length;
    if (count > 8) {
      return t(`DOWNLOAD_BUTTON.PDF_MULTI_REPORT_EXCEED`, { ns: 'bearing-hub/bearingHub' });
    }
    if (count === 0) {
      return '';
    }
    const numberOfReports = count === 8 ? '8/8' : `${count}/8`;
    return `${t(`DOWNLOAD_BUTTON.PDF_MULTI_REPORT`, { ns: 'bearing-hub/bearingHub' })} ${numberOfReports}`;
  };

  return (
    <div>
      <div className="lg:hidden flex flex-col" data-testid="product-overview-container-table-mobile">
        {totalPages || isLoading ? (
          <ProductTileList
            currentPage={currentPage}
            maxLifeTimeReference={lifetimeBarReference}
            lastKnownValidResponse={tableData}
            quantityInputValue={quantity}
            selectedShape={selectedShape}
            setActiveModal={setActiveModal}
            unitSystem={unitSystem}
            bearingPartsLifetime={bearingPartsLifetime}
            isLifetimeLoading={isLifetimeLoading}
            clientReady={clientReady}
            onQuantityChange={onQuantityChange}
          />
        ) : (
          <>
            <NothingFound
              dimensionFormIsCompleted={dimensionsFormIsCompleted()}
              mobile
              selectedShapeKey={selectedShape.shapeKey}
            />
            {similarParts?.length > 0 && (
              <SimilarPartsTable
                similarParts={similarParts}
                selectedShape={selectedShape}
                onChoose={onChoose}
                isLink={false}
                dimensions={dimensions[selectedShape.shapeKey]}
                unitSystem={unitSystem}
              />
            )}
          </>
        )}
      </div>
      <div className="lg:hidden relative z-[100]">{clientReady && <ProductSort />}</div>
      <div className="hidden lg:block relative" data-testid="product-overview-container-table">
        {totalPages || isLoading ? (
          <BearingsDesktopTable
            isLoading={isLoading}
            lastKnownValidResponse={lastKnownValidResponse}
            currentPage={currentPage}
            maxLifeTimeReference={lifetimeBarReference}
            bearingPartsResponseStatus={bearingPartsResponseStatus}
            quantity={quantity}
            selectedShape={selectedShape}
            setActiveModal={setActiveModal}
            clientReady={clientReady}
            unitSystem={unitSystem}
            articlesAddingToBasket={articlesAddingToBasket}
            addToShoppingCart={addToShoppingCart}
            bearingPartsLifetime={bearingPartsLifetime}
            isLifetimeLoading={isLifetimeLoading}
            tableData={tableData}
            tableHeaders={tableHeaders}
            expandedRows={expandedRows}
            selectedParts={selectedParts}
            onExpandRow={handleRowExpand}
            onSelectPart={handlePartSelection}
            onClearSelection={() => setSelectedParts([])}
          />
        ) : (
          <>
            <NothingFound
              dimensionFormIsCompleted={dimensionsFormIsCompleted()}
              selectedShapeKey={selectedShape.shapeKey}
            />
            {similarParts?.length > 0 && (
              <SimilarPartsTable
                similarParts={similarParts}
                selectedShape={selectedShape}
                onChoose={onChoose}
                isLink={false}
                dimensions={dimensions[selectedShape.shapeKey]}
                unitSystem={unitSystem}
              />
            )}
          </>
        )}
      </div>

      <div className="fixed w-full bottom-8 left-1/2 z-10 transform -translate-x-1/2">
        <div className="flex justify-center">
          {selectedParts.length > 0 && (
            <div>
              <Button
                type={TYPE.PRIMARY}
                text={t(`COMMON.ADD_SELECTION_TO_CART`, { ns: 'bearing-hub/bearingHub' })}
                className="w-[290px] mx-2 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.3),0px_4px_8px_3px_rgba(0,0,0,0.15)]"
                data-testid="add-to-basket-multi-parts"
                onClick={() => onAddToShoppingCartSelectedParts()}
                loading={articlesAddingToBasket.length !== 0}
              />
              {showMultiReportOption && (
                <Button
                  type={TYPE.TERTIARY}
                  text={multiReportText()}
                  disabled={selectedParts.length > 8}
                  className="w-[290px] mx-2 bg-white shadow-[0px_1px_3px_0px_rgba(0,0,0,0.3),0px_4px_8px_3px_rgba(0,0,0,0.15)]"
                  data-testid="download-multi-report"
                  onClick={() => downloadMultiReport()}
                  loading={!!loadingDownloadItem.size}
                />
              )}
            </div>
          )}
          {expandedRows.size > 1 && (
            <Button
              type={TYPE.SECONDARY}
              text={t(`COLLAPSE_ALL`, { ns: 'bearing-hub/bearingHub' })}
              className="w-[290px] mx-2 shadow-[0px_1px_3px_0px_rgba(0,0,0,0.3),0px_4px_8px_3px_rgba(0,0,0,0.15)]"
              data-testid="collapse-all-rows"
              onClick={() => setExpandedRows(new Set())}
            />
          )}
        </div>
      </div>
    </div>
  );
};
